import React from 'react';
import moment from 'moment-timezone';

import {Consultant} from './Section/Consultant';
import Factoid from './Section/Factoid';
import {VisceralFat} from './Section/Illustration/VisceralFat';
import Posts from './Section/Posts';
import Tutorial from './Section/Tutorial';
import {SCAN_IMAGES_ANCHOR} from './Summary/InfoCard/ImagesCard';
import {GramsDensityIcon} from './Summary/InfoCard/Stats/Units';
import {KiloDensityIcon} from './Summary/InfoCard/Stats/Units';
import {Dexa} from './query';
import {CardText, Configs, DexaMetric} from './types';

export type Inputs = {
	appts: string[],
	filter: {
		gender: string,
		min_age: number,
		max_age: number,
		handle?: string
	},
};

type RefOptions = {
	same?: boolean
	cnt?: number
}

const reference = (ins: Inputs, opts?: RefOptions): string => {
	const gender = ins.filter.gender === 'Male' ? 'men' : 'women';
	if (opts?.same) {
		return `${gender} of the same age`;
	}

	let count = '';
	if (opts?.cnt) {
		const rounded = Math.ceil(opts.cnt / 1000) * 1000;
		count = rounded.toLocaleString() + ' ';
	}

	return count +
		`${gender}, ages ${ins.filter.min_age} to ` +
		`${ins.filter.max_age}`;
};

const explainer = (snap?: string): string => {
	if (snap) {
		return `Compare your ${snap} to others in your selected gender & ` +
			'age reference group. Use the dropdown to toggle between scans.';
	}
	return 'See your changes over time and where you fall compared ' +
		'to others of your selected gender.';
};

const disclaimer = 'The information presented in the BodySpec report is not intended to replace medical advice, nor to diagnose, prescribe, or treat any disease, condition, illness, or injury.';

const cardText = (metric: string): CardText => {
	// eslint-disable-next-line react/display-name
	return (pct, reverse, breaks) => {
		if (!breaks) return null;

		if (!reverse) {
			// Green
			if (pct < breaks[1]) {
				return (
					<>
						You're in the lowest <em>{pct}%</em> of
						people for {metric}
					</>
				);
			}

			// Red & Yellow
			return (
				<>
					<em>{100 - pct}%</em> of people have
					higher {metric} than you
				</>
			);
		}

		// Red
		if (pct < breaks[1]) {
			return (
				<>
					You're in the bottom <em>{pct}%</em> of
					people for {metric}
				</>
			);
		}

		// Green + Yellow
		return (
			<>
				You have higher {metric}{' '}
				than <em>{pct}%</em> of people
			</>
		);
	};
};

export const configs = (ins: Inputs, dexa?: Dexa): Configs => ({
	header: (() => {
		const dateify = (v?: number) => moment(v).format('MM/DD/YY');
		const shared = {
			count: ins.appts.length,
			reference: reference(ins, {cnt: dexa?.reference}),
			handle: ins.filter.handle,
		};

		if (dexa?.created) {
			return {
				...shared,
				generated: dateify(Number(dexa.created.unix) * 1000),
			};
		}

		return {
			...shared,
			generated: dateify(),
		};
	})(),
	sections: [{
		color: 'blue',
		details: {
			header: {
				title: 'Using this Report',
				summary: 'Learn to navigate and customize your data for maximum insight.',
			},
			snapshot: {
				chart: {
					hover: '',
					marker: '',
					xlabel: '',
				},
				text: {
					explainer: '',
					metric: '',
					reference: '',
				},
			},
			historical: {
				chart: {
					hover: () => '',
					marker: '',
					ylabel: '',
				},
				text: {
					explainer: '',
					reference: '',
				},
			},
			table: {
				title: '',
				heading: '',
				fmt: () => '',
			},
			percentiles: {
				label: '',
			},
			footer: <Tutorial />,
		},
		separator: false,
	},
	{
		color: 'green',
		open: 'always',
		exclude: 'voice',
		details: {
			header: {
				title: 'Try our AI consultant',
				summary: '',
			},
			snapshot: {
				chart: {
					hover: '',
					marker: '',
					xlabel: '',
				},
				text: {
					explainer: '',
					metric: '',
					reference: '',
				},
			},
			historical: {
				chart: {
					hover: () => '',
					marker: '',
					ylabel: '',
				},
				text: {
					explainer: '',
					reference: '',
				},
			},
			table: {
				title: '',
				heading: '',
				fmt: () => '',
			},
			percentiles: {
				label: '',
			},
			footer: <Consultant />,
		},
		separator: false,
	},
	{
		anchor: SCAN_IMAGES_ANCHOR,
		open: true,
		details: {
			header: {
				title: 'Scan Images',
				summary: 'View your x-ray scan images showing lean tissue, fat, and bone distribution. **Not for diagnostic use.**',
			},
			footer: 'scan-images',
			...fillerDetails(),
		},
		separator: true,
	},
	{
		metric: DexaMetric.FatPct,
		details: {
			header: {
				title: 'Body Fat Percentage',
				summary: 'The percentage of your total weight that is fat, including subcutaneous, visceral, and brown fat tissue.',
			},
			citations: {
				methodology: '',
				papers: [
					'- Frank, A. P., de Souza Santos, R., Palmer, B. F., & Clegg, D. J. (2019). Determinants of body fat distribution in humans may provide insight about obesity-related health risks. *Journal of lipid research*, 60(10), 1710-1719.',
					'- Mondal, H., & Mishra, S. P. (2017). Effect of BMI, Body Fat Percentage and Fat Free Mass on Maximal Oxygen Consumption in Healthy Young Adults. *Journal of clinical and diagnostic research* : JCDR, 11(6), CC17–CC20.',
					'- Gijs H. Goossens; The Metabolic Phenotype in Obesity: Fat Mass, Body Fat Distribution, and Adipose Tissue Function. *Obes Facts* 13 July 2017; 10 (3): 207–215.',
					'- Bigaard, J., Frederiksen, K., Tjønneland, A., Thomsen, B.L., Overvad, K., Heitmann, B.L. and Sørensen, T.I.A. (2004), Body Fat and Fat-Free Mass and All-Cause Mortality. *Obesity Research*, 12: 1042-1049.'
				],
				disclaimer: disclaimer,
			},
			card: {
				title: 'Body fat %',
				fmt: (v) => [v.toFixed(1), <span key='🙃'>%</span>],
				description: cardText('body fat %'),
			},
			snapshot: {
				chart: {
					hover: '~%{text:.1f}% have less than %{x:.1f}% body fat',
					marker: '%{x:.1f}%',
					xlabel: '% body fat',
				},
				text: {
					explainer: explainer('body fat %'),
					metric: 'body fat %',
					reference: reference(ins, {cnt: dexa?.reference}),
				},
			},
			historical: {
				chart: {
					hover: (min, max) => (
						`${min.toFixed(1)}% to ${max.toFixed(1)}%`
					),
					marker: '%{y:.1f}%',
					ylabel: 'Body fat %',
					yrange: ins.filter.gender === 'Male' ? [5, 45] : [10, 55],
					minAge: ins.filter.min_age,
					maxAge: ins.filter.max_age
				},
				text: {
					explainer: explainer(),
					reference: reference(ins, {same: true}),
				},
			},
			table: {
				arrow: true,
				title: 'Body fat % by scan',
				heading: 'Body fat %',
				fmt: (v) => `${v.toFixed(1)}%`,
			},
			percentiles: {
				label: 'body fat %',
			},
			footer: (
				<>
					<Factoid
						title='What to know about fat?'
						text='Nutrition is often the biggest factor in reducing body fat.  Focusing on adequate protein, a sustainable caloric deficit, and including as many healthy foods as possible is a good place to start.'
					/>
					<Posts
						slugs={[
							'subcutaneous_fat_what_it_is_and_how_to_reduce_it',
							'what_does_body_fat_percentage_really_mean',
							'lose_fat_and_gain_muscle_is_it_possible',
						]}
					/>
				</>
			)
		}
	},
	{
		metric: DexaMetric.RegionalComp,
		details: {
			header: {
				title: 'Regional composition',
				summary: 'Shows fat, muscle, and bone distribution to identify risks, guide training, and personalize interventions for better results.',
			},
			citations: {
				methodology: '',
				papers: [
					'- Snijder, M. B., Visser, M., Dekker, J. M., Seidell, J. C., Fuerst, T., Lang, T., ... & Harris, T. B. (2004). Association between regional body fat and cardiovascular disease risk among postmenopausal women with normal body mass index. European Heart Journal, 25(7), 533-540.',
					'- Scafoglieri, A., & Clarys, J. P. (2014). Use of anthropometry for the prediction of regional body tissue distribution in adults: Benefits and limitations in clinical practice. Aging and Disease, 5(6), 373-393.',
					'- Nana A, Slater GJ, Stewart AD, Burke LM. International Journal of Sport Nutrition and Exercise Metabolism. 2015;25(3):213-222.',
					'- Elliott SA, et al. Clinical Nutrition. 2020;39(5):1512-1518.',
				],
				disclaimer: disclaimer,
			},
			card: {
				title: 'Regional comp',
				fmt: () => ['', null],
				description: () => '',
			},
			footer: 'regional-comp',
			...fillerDetails(),
		},
		separator: true,
	},
	{
		metric: DexaMetric.VisceralFat,
		details: {
			header: {
				title: 'Visceral Fat (VAT)',
				summary: '"Hidden" fat surrounding internal organs linked to increased risk of heart disease, type 2 diabetes, and certain cancers.',
			},
			citations: {
				methodology: 'This data is based on 250K+ scans and their data from clients of all ages and includes self-reported men and women breakdowns. Studies cited below show there is no healthy level of visceral fat, and that visceral fat is the most accurate indicator of risk for metabolic disease.  Percentile groups and color ranges were derived from where we see visceral fat levels distributed across the 100\'s thousands of scans we\'ve performed. Any improvement in visceral fat levels is a positive step towards better health outcomes.',
				papers: [
					'- Mathieu, P., Poirier, P., Pibarot, P., Lemieux, I., & Després, J. P. (2009). Visceral obesity: the link among inflammation, hypertension, and cardiovascular disease. *Hypertension*, 53(4), 577-584.',
					'- Matsuzawa, Y., Nakamura, T., Shimomura, I., & Kotani, K. (1995). Visceral fat accumulation and cardiovascular disease. *Obesity research*, 3(S5), 645S-647S.',
					'- Rosito, G. A., Massaro, J. M., Hoffmann, U., Ruberg, F. L., Mahabadi, A. A., Vasan, R. S., ... & Fox, C. S. (2008). Pericardial fat, visceral abdominal fat, cardiovascular disease risk factors, and vascular calcification in a community-based sample: the Framingham Heart Study. *Circulation*, 117(5), 605-613.',
					'- Chiba, Y., Saitoh, S., Takagi, S., Ohnishi, H., Katoh, N., Ohata, J., ... & Shimamoto, K. (2007). Relationship between visceral fat and cardiovascular disease risk factors: the Tanno and Sobetsu study. *Hypertension Research*, 30(3), 229-236.'
				],
				disclaimer: disclaimer,
			},
			illustration: <VisceralFat />,
			card: {
				title: 'Visceral fat',
				fmt: (v) => [v.toFixed(2), <span key='🙃'>lbs</span>],
				description: cardText('visceral fat'),
			},
			snapshot: {
				chart: {
					hover: '~%{text:.1f}% have %{x:.2f} or less lbs of VAT',
					marker: '%{x:.1f} lbs',
					xlabel: 'Visceral fat (lbs)',
				},
				text: {
					explainer: explainer('visceral fat'),
					metric: 'visceral fat (lbs)',
					reference: reference(ins, {cnt: dexa?.reference}),
				},
			},
			historical: {
				chart: {
					hover: (min, max) => (
						`${min.toFixed(1)} to ${max.toFixed(1)} lbs`
					),
					marker: '%{y:.1f} lbs',
					min: 0.05,
					ylabel: 'Visceral fat (lbs)',
					minAge: ins.filter.min_age,
					maxAge: ins.filter.max_age,
				},
				text: {
					explainer: explainer(),
					reference: reference(ins, {same: true}),
				},
			},
			table: {
				arrow: true,
				title: 'Visceral fat (lbs) by scan',
				heading: 'Visceral fat',
				fmt: (v) => `${v.toFixed(1)} lbs`,
			},
			percentiles: {
				label: 'visceral fat',
			},
			footer: (
				<>
					<Factoid
						title='How can I reduce visceral fat?'
						text='Maintaining low levels of visceral fat can seriously reduce your risk for chronic conditions such as heart disease, type-2 diabetes, and some forms of cancer. Reducing alcohol intake, smoking, refined sugars and carbohydrates, and ultra-processed foods with trans fats can help to decrease visceral fat.'
					/>
					<Posts
						slugs={[
							'visceral_fat_percentile_charts',
							'what_is_a_healthy_visceral_fat_level',
							'visceral_fat_level_chart_understanding_your_health_risks',
						]}
					/>
				</>
			)
		}
	},
	{
		metric: DexaMetric.LeanMass,
		reverse: true,
		details: {
			header: {
				title: 'Lean Mass (BMI-L)',
				summary: 'Everything except fat and bone. Supports strength and metabolism. BMI-L is lean mass (kg) divided by height (m) squared.',
			},
			citations: {
				methodology: '',
				papers: [
					'- Alfonso J Cruz-Jentoft, Gülistan Bahat, Jürgen Bauer, Yves Boirie, Olivier Bruyère, Tommy Cederholm, Cyrus Cooper, Francesco Landi, Yves Rolland, Avan Aihie Sayer, Stéphane M Schneider, Cornel C Sieber, Eva Topinkova, Maurits Vandewoude, Marjolein Visser, Mauro Zamboni, Writing Group for the European Working Group on Sarcopenia in Older People 2 (EWGSOP2), and the Extended Group for EWGSOP2 , Sarcopenia: revised European consensus on definition and diagnosis, *Age and Ageing*, Volume 48, Issue 1, January 2019, Pages 16–31, ',
					'- Chen, Z., Lohman, T.G., Stini, W.A., Ritenbaugh, C. and Aickin, M. (1997), Fat or Lean Tissue Mass: Which One Is the Major Determinant of Bone Mineral Mass in Healthy Postmenopausal Women?. *J Bone Miner Res*, 12: 144-151. ',
					'- Pasco, J., Brennan-Olsen, S., Kew, K., Hyde, N. K., & Kotowicz, M. (2016). Low lean tissue mass and physical performance as markers of sarcopenia in older men and women.',
					'- Sparti A, DeLany JP, de la Bretonne JA, Sander GE, Bray GA. Relationship between resting metabolic rate and the composition of the fat-free mass. *Metabolism*. 1997 Oct;46(10):1225-30.'
				],
				disclaimer: disclaimer,
			},
			card: {
				title: 'Lean mass',
				fmt: (v) => [v.toFixed(1), <KiloDensityIcon key='👯‍♀️' />],
				description: cardText('avg lean mass'),
			},
			snapshot: {
				chart: {
					hover: '~%{text:.1f}% have a BMI-L less than %{x:.1f}',
					marker: '%{x:.1f} BMI-L',
					xlabel: 'Lean mass index (kg/m<sup>2</sup>)',
				},
				text: {
					explainer: explainer('BMI-L'),
					metric: 'BMI-L',
					reference: reference(ins, {cnt: dexa?.reference}),
				},
			},
			historical: {
				chart: {
					hover: (min, max) => (
						`${min.toFixed(1)} to ${max.toFixed(1)} kg/m<sup>2</sup>`
					),
					marker: '%{y:.1f} BMI-L',
					ylabel: 'Lean mass index (kg/m<sup>2</sup>)',
					yrange: ins.filter.gender === 'Male' ? [13, 27] : [11, 23],
					minAge: ins.filter.min_age,
					maxAge: ins.filter.max_age
				},
				text: {
					explainer: explainer(),
					reference: reference(ins, {same: true}),
				},
			},
			table: {
				arrow: true,
				title: 'Lean mass index by scan',
				heading: 'Lean mass index',
				fmt: (v) => <>{v.toFixed(1)} kg/m<sup>2</sup></>,
			},
			percentiles: {
				label: 'lean mass',
			},
			footer: (
				<>
					<Factoid
						title='Lean Mass Index (BMI-L)'
						text='Lean Mass Index is a height normalized measurement of your lean mass. This is similar to BMI, but it excludes your body fat from the measurement to provide a more accurate picture of your health. You can [learn more about BMI-L on our blog](/blog/post/bmi_is_out_bfi_is_in_why_bodyspec_created_a_better_way_to_measure_your_health).'
					/>
					<Posts
						slugs={[
							'gaining_lean_muscle_a_comprehensive_guide',
							'muscle_vs_lean_what_does_it_mean',
							'5_super_quick_tips_on_lean_tissue_for_beginners',
						]}
					/>
				</>
			)
		},
	},
	{
		metric: DexaMetric.BoneDensity,
		reverse: true,
		details: {
			header: {
				title: 'Bone Density',
				summary: 'Measures fracture risk by comparing bone strength to population data. Not diagnostic, but helps assess general risk.',
			},
			citations: {
				methodology: '',
				papers: [
					'- Kanis, J. A., McCloskey, E. V., Johansson, H., Oden, A., Melton III, L. J., & Khaltaev, N. (2008). A reference standard for the description of osteoporosis. *Bone*, 42(3), 467-475.',
					'- Deng, H. W., Xu, F. H., Davies, K. M., Heaney, R., & Recker, R. R. (2002). Differences in bone mineral density, bone mineral content, and bone areal size in fracturing and non-fracturing women, and their interrelationships at the spine and hip. *Journal of bone and mineral metabolism*, 20(6), 358-366.',
					'- Kroger, H., & Laitinen, K. (1992). Bone mineral density measured by dual‐energy X‐ray absorptiometry in normal men. *European journal of clinical investigation*, 22(7), 454-460.',
				],
				disclaimer: disclaimer,
			},
			card: {
				title: 'Bone density',
				fmt: (v) => [v.toFixed(2), <GramsDensityIcon key='🤷‍♀️' />],
				description: cardText('bone density'),
			},
			snapshot: {
				chart: {
					hover: '~%{text:.1f}% have a bone density less than %{x:.2f} g/cm<sup>2</sup>',
					marker: '%{x:.2f} g/cm<sup>2</sup>',
					xlabel: 'Bone density (g/cm<sup>2</sup>)',
				},
				text: {
					explainer: explainer('bone density'),
					metric: 'bone density',
					reference: reference(ins, {cnt: dexa?.reference}),
				},
			},
			historical: {
				chart: {
					hover: (min, max) => (
						`${min.toFixed(2)} to ${max.toFixed(2)} g/cm<sup>2</sup>`
					),
					marker: '%{y:.2f} g/cm<sup>2</sup>',
					ylabel: 'Bone density (g/cm<sup>2</sup>)',
					yrange: ins.filter.gender === 'Male' ? [0.9, 1.9] : [0.8, 1.5],
					minAge: ins.filter.min_age,
					maxAge: ins.filter.max_age
				},
				text: {
					explainer: explainer(),
					reference: reference(ins, {same: true}),
				},
			},
			table: {
				title: 'Bone density by scan',
				heading: 'Bone density',
				fmt: (v) => <>{v.toFixed(2)} g/cm<sup>2</sup></>,
			},
			percentiles: {
				label: 'bone density',
			},
			footer: (
				<>
					<Factoid
						title='How can I maintain healthy bones?'
						text='Strong bones need more than milk. Weight-bearing activities—like walking or lifting—spur growth, while adequate vitamin D, balanced calcium, healthy hormones, and a nutrient-rich diet help preserve density, prevent fractures, and support long-term mobility.'
					/>
					<Posts
						slugs={[
							'bones_make_it_or_break_it',
							'a_guide_to_osteopenia',
							'the_hard_facts_about_bone',
						]}
					/>
				</>
			)
		},
	},
	{
		metric: DexaMetric.RmrEstimate,
		details: {
			header: {
				title: 'Resting Metabolic Rate (RMR)',
				summary: 'Calories burned at rest. Part of Total Daily Energy Expenditure (TDEE) and helps estimate calories needed to maintain weight.',
			},
			citations: {
				methodology: '',
				papers: [
					'- ten Haaf T, Weijs PJ. (2014). Resting energy expenditure prediction in recreational athletes of 18-35 years: confirmation of Cunningham equation and an improved weight-based alternative. PLoS One. 2014 Oct 2;9(9):e108460.',
					'- Cunningham, J.J. (1980). A reanalysis of the factors influencing basal metabolic rate in normal adults. The American Journal of Clinical Nutrition, 33(11), 2372–2374.',
					'- De Lorenzo A, Bertini L, Candeloro N, Piccinelli R, Innocente L, et al. (1999) A new predictive equation to calculate resting metabolic rate in athletes. J Sports Med Phys Fitness 39: 213–219.',
					'- Mifflin, M.D., St Jeor, S.T., Hill, L.A., Scott, B.J., Daugherty, S.A., & Koh, Y.O. (1990). A new predictive equation for resting energy expenditure in healthy individuals. The American Journal of Clinical Nutrition, 51(2), 241–247.',
				],
				disclaimer: disclaimer,
			},
			card: {
				title: 'Resting metabolic',
				fmt: (v, p) => [
					(p.match(/^[^#]+#(\d+)/)?.[1] || '0'),
					<span key='🙃'>cal</span>,
				],
				description: () => 'We recommend using RMR as a tool to understand your metabolism. There\'s no "good" or "bad" RMR.',
			},
			footer: 'rmr-estimate',
			...fillerDetails(),

			historical: {
				chart: {
					hover: () => '',
					marker: '',
					ylabel: '',
					yrange: [800, 2600],
					minAge: ins.filter.min_age,
					maxAge: ins.filter.max_age
				},
				text: {
					explainer: '',
					reference: '',
				},
			},
		},
		separator: true,
	},
	{
		metric: DexaMetric.LimbMass,
		reverse: true,
		details: {
			header: {
				title: 'Limb / Appendicular Lean Mass (ALMI)',
				summary: 'Estimates muscle mass from arms/legs and linked to strength and mobility. ALMI is limb lean mass (kg) divided by height (m) squared.',
			},
			citations: {
				methodology: '',
				papers: [
					'- Gould, H., Brennan, S.L., Kotowicz, M.A. et al. Total and Appendicular Lean Mass Reference Ranges for Australian Men and Women: The Geelong Osteoporosis Study. Calcif Tissue Int 94, 363–372 (2014).',
					'- Yun-Mi Song, Kayoung Lee, Comparison of the associations between appendicular lean mass adjustment methods and cardiometabolic factors,Nutrition, Metabolism and Cardiovascular Diseases, Volume 30, Issue 12, 2020',
					'- Cawthon PM. Assessment of Lean Mass and Physical Performance in Sarcopenia. J Clin Densitom. 2015 Oct-Dec;18(4):467-71. doi: 10.1016/j.jocd.2015.05.063. Epub 2015 Jun 10. PMID: 26071168.',
				],
				disclaimer: disclaimer,
			},
			card: {
				title: 'Limb lean mass',
				fmt: (v) => [v.toFixed(1), <KiloDensityIcon key='👯‍♀️' />],
				description: cardText('avg limb lean'),
			},
			snapshot: {
				chart: {
					hover: '~%{text:.1f}% have an ALMI less than %{x:.2f} kg/m<sup>2</sup>',
					marker: '%{x:.2f} kg/m<sup>2</sup>',
					xlabel: 'ALMI (kg/m<sup>2</sup>)',
				},
				text: {
					explainer: explainer('ALMI'),
					metric: 'ALMI',
					reference: reference(ins, {cnt: dexa?.reference}),
				},
			},
			historical: {
				chart: {
					hover: (min, max) => (
						`${min.toFixed(2)} to ${max.toFixed(2)} kg/m<sup>2</sup>`
					),
					marker: '%{y:.2f} kg/m<sup>2</sup>',
					ylabel: 'ALMI (kg/m<sup>2</sup>)',
					yrange: ins.filter.gender === 'Male' ? [6, 15] : [5, 10],
					minAge: ins.filter.min_age,
					maxAge: ins.filter.max_age
				},
				text: {
					explainer: explainer(),
					reference: reference(ins, {same: true}),
				},
			},
			table: {
				title: 'ALMI by scan',
				heading: 'ALMI',
				fmt: (v) => <>{v.toFixed(2)} kg/m<sup>2</sup></>,
			},
			percentiles: {
				label: 'ALMI',
			},
			footer: (
				<>
					<Factoid
						title='Why care about ALMI?'
						text='Appendicular lean mass index reveals subtle muscle health shifts that basic measures miss. It can detect early frailty or sarcopenia, providing clues about long-term strength, mobility, and future health risks beyond simple BMI.'
					/>
					<Posts
						slugs={[
							'why_is_appendicular_lean_mass_important',
							'appendicular_lean_mass_and_almilongevity',
							'appendicular_lean_mass_and_sarcopenia',
						]}
					/>
				</>
			)
		},
	}]
});

const fillerDetails = () => ({
	snapshot: {
		chart: {
			hover: '',
			marker: '',
			xlabel: '',
		},
		text: {
			explainer: '',
			metric: '',
			reference: '',
		},
	},
	historical: {
		chart: {
			hover: () => '',
			marker: '',
			ylabel: '',
		},
		text: {
			explainer: '',
			reference: '',
		},
	},
	table: {
		title: '',
		heading: '',
		fmt: () => '',
	},
	percentiles: {
		label: '',
	},
});
